import { Service } from '@/types/services';

// 1. AI & Automation
export const aiAutomationServices: Service[] = [
  {
    id: 'ai-chatbots',
    title: 'AI Chatbots & Assistants',
    icon: 'Brain',
    shortDescription: 'Intelligent conversational agents that improve customer service and automate tasks.',
    description: 'Our AI chatbots and automation solutions provide 24/7 support, automate workflows, and deliver actionable insights with advanced analytics. We use OpenAI, Dialogflow, and custom bots for seamless integration.',
    offerings: [
      { name: 'Customer Support Bots', description: 'Automated support for common questions and issues.' },
      { name: 'Internal Knowledge Assistants', description: 'AI tools for quick information retrieval.' },
      { name: 'Sales & Lead Generation Bots', description: 'Conversational agents for lead qualification and booking.' },
      { name: 'Custom AI Assistant Development', description: 'Tailored AI solutions for your business.' },
      { name: 'Chatbot Analytics & Optimization', description: 'Continuous improvement through analytics.' },
      { name: 'Business Process Automation', description: 'AI-powered workflow automation for efficiency.' },
      { name: 'Document Processing & Analysis', description: 'AI extraction and processing of business documents.' },
      { name: 'Predictive Analytics', description: 'Forecast outcomes and trends using machine learning.' },
      { name: 'Anomaly Detection', description: 'Identify unusual patterns for fraud or opportunity.' },
      { name: 'Custom AI Dashboard Development', description: 'Visualize AI insights for decision-making.' }
    ],
    benefits: [
      '24/7 availability and support',
      'Reduced operational costs',
      'Scalable automation',
      'Deeper business insights',
      'Improved customer experience',
      'Competitive advantage through AI',
      'Data-driven decision making',
      'Elimination of human error in routine tasks',
      'Cost savings through operational efficiency',
      'Integration with existing business systems'
    ],
    image: 'https://images.unsplash.com/photo-1531746790731-6c087fecd65a',
    category: 'virtualAssistance'
  }
];

// 2. Software & Tech
export const softwareTechServices: Service[] = [
  {
    id: 'web-software-development',
    title: 'Web Design & Software Development',
    icon: 'Code2',
    shortDescription: 'Custom-coded, high-performance web and software solutions. No page builders or WordPress.',
    description: 'We hand-write every line of code for blazing fast, secure, and fully custom websites and apps. No bloat, no plugins, just results. Our solutions load instantly, are more secure, and are optimized for Google rankings.',
    offerings: [
      { name: 'Custom Web Design', description: 'Hand-coded, unique websites with zero bloat.' },
      { name: 'Web App Development', description: 'Modern, scalable web applications.' },
      { name: 'Mobile App Development', description: 'Cross-platform mobile apps for iOS and Android.' },
      { name: 'AI Integration', description: 'Add AI features to your apps and sites.' },
      { name: 'Ongoing Support & Maintenance', description: 'Continuous updates and technical support.' }
    ],
    benefits: [
      'Faster load times',
      'Better conversions',
      'Better performing ads',
      'More secure',
      'Higher Google rankings',
      'Boosts your Google Business Profile',
      'Unique, high-performing site',
      'Capture more leads',
      'No plugin or update worries',
      'Save money on ads with better landing pages'
    ],
    image: 'https://images.unsplash.com/photo-1461749280684-dccba630e2f6',
    category: 'software'
  }
];

// 3. SEO & Analytics
export const seoAnalyticsServices: Service[] = [
  {
    id: 'seo-analytics',
    title: 'SEO & AI-Powered Analytics',
    icon: 'TrendingUp',
    shortDescription: 'Boost your online visibility and gain actionable insights with expert SEO and analytics.',
    description: 'We help you rank higher, drive more traffic, and understand your audience with advanced SEO and AI analytics. Our strategies are data-driven and tailored for your business.',
    offerings: [
      { name: 'SEO Audit', description: 'Comprehensive technical and content audit.' },
      { name: 'Keyword Research & Strategy', description: 'Find and target valuable search terms.' },
      { name: 'On-Page & Technical Optimization', description: 'Optimize content, meta tags, and site structure.' },
      { name: 'Content Strategy', description: 'SEO-friendly content that ranks and converts.' },
      { name: 'Performance Tracking', description: 'Monitor SEO metrics and keyword rankings.' },
      { name: 'AI Analytics', description: 'Predictive analytics and custom dashboards.' },
      { name: 'Backlinking', description: 'Quality link building for authority.' },
      { name: 'Local SEO', description: 'Optimize your Google Business Profile and Maps presence.' }
    ],
    benefits: [
      'Increased organic search visibility',
      'Higher quality website traffic',
      'Improved conversion rates',
      'Better user experience',
      'Long-term sustainable results',
      'Data-driven decision making',
      'Early identification of business opportunities',
      'Improved accuracy in forecasting',
      'Competitive advantage through analytics',
      'Optimization of marketing spend'
    ],
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71',
    category: 'seoai'
  },
  {
    id: 'google-ads-management',
    title: 'Google Ads Management',
    icon: 'Target',
    shortDescription: 'Flat $300/month Google Ads management for Dallas–Fort Worth and US businesses. Real humans, weekly tuning, no contracts.',
    description: 'A small Google Ads management company serving Dallas–Fort Worth and the US: campaign management, PPC, and Google Shopping Ads. We cap our roster so every account gets weekly attention from the person actually running it. 9.2% average CTR, $3.56 average CPC, no annual contracts.',
    offerings: [
      { name: 'Account Setup, End to End', description: 'Conversion tracking, audience baselines, negative keyword seed list, ad extensions, geo-targeting — built inside your account.' },
      { name: 'Keyword Research', description: 'Hand-written keyword lists from search-term data with match-type strategy and budget-burner kill list.' },
      { name: 'Ad Copywriting', description: 'Three to five variants per ad group, written for actual user intent — not generic templates.' },
      { name: 'Weekly Search-Term Review', description: 'Negative keywords added every week so spend stops leaking into junk queries.' },
      { name: 'Bid & Budget Tuning', description: 'Adjustments based on what is actually converting — not Google\'s recommendations.' },
      { name: 'Monthly Written Report', description: 'A short note explaining what worked, what didn\'t, and what is changing next.' },
      { name: 'Google Shopping Ads Management', description: 'Product feed setup and optimization for e-commerce campaigns.' },
      { name: 'PPC Campaign Management', description: 'Full-funnel PPC across Search, Shopping, and Performance Max.' }
    ],
    benefits: [
      'Flat $300/month — no percentage of ad spend',
      'No annual contracts, cancel with 30 days notice',
      'You own your Google Ads account from day one',
      '9.2% average CTR — 3× the industry standard',
      'Weekly optimization by a real human',
      'Direct access to the person running your campaign',
      'Real reports written by hand, not auto-generated',
      'Free initial campaign audit, no obligation'
    ],
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f',
    category: 'seoai',
    customLink: '/google-ads-management'
  }
];

// 4. Creative
export const creativeServices: Service[] = [
  {
    id: 'creative-content',
    title: 'Creative Design, Content & AI Video Generation',
    icon: 'Palette',
    shortDescription: 'Branding, design, content writing, and AI-powered video creation.',
    description: 'We create stunning visuals, compelling content, and next-gen AI videos to elevate your brand. Our creative team uses the latest tools for design, copy, and video.',
    offerings: [
      { name: 'Brand Identity Design', description: 'Logo, color palette, typography, brand guidelines.' },
      { name: 'Marketing Material Design', description: 'Brochures, flyers, banners, business cards.' },
      { name: 'Content Writing', description: 'Website copy, articles, scripts, proofreading.' },
      { name: 'AI Video Generation', description: 'AI-powered video creation for marketing and social.' },
      { name: 'Social Media Content', description: 'Graphics, animations, and posts for all platforms.' }
    ],
    benefits: [
      'Consistent brand presentation',
      'Increased brand recognition',
      'Higher engagement rates',
      'Professional appearance',
      'Content that converts',
      'AI-powered video for modern marketing',
      'Improved search engine rankings',
      'Clear communication of complex topics',
      'Content that converts visitors to customers',
      'Consistent brand voice across all materials'
    ],
    image: 'https://images.unsplash.com/photo-1561070791-2526d30994b5',
    category: 'creatives'
  }
];

// 5. AI Workflows & Automation
export const aiWorkflowServices: Service[] = [
  {
    id: 'ai-workflows',
    title: 'AI Workflows & Automation',
    icon: 'Brain',
    shortDescription: 'n8n workflows, document chatbots and AI integrations that remove manual work from your business.',
    description: 'We connect the tools you already use and put AI to work on the repetitive parts of your day: automated workflows, assistants that answer from your own documents, and integrations that let Claude or ChatGPT act inside your systems. It is the same approach we use to build our own product, HR FLARE.',
    offerings: [
      { name: 'n8n Workflow Automation', description: 'Connect your CRM, email, calendars, storage and forms so data stops being retyped.' },
      { name: 'Document Chatbots (RAG)', description: 'Assistants that answer from your own files and data, with sources.' },
      { name: 'MCP Integrations', description: 'Let Claude or ChatGPT work directly inside your business systems, within set permissions.' },
      { name: 'Document OCR & Extraction', description: 'Read receipts, CVs and forms and turn them into structured data.' },
      { name: 'Custom Internal Tools', description: 'Dashboards, portals and admin tools built around how your team works.' }
    ],
    benefits: [
      'Hours of manual work removed every week',
      'Fewer copy-paste errors between systems',
      'Answers from your own data, not guesses',
      'Access limited to what each user may see',
      'Works with the tools you already pay for',
      'Built and proven on our own production product'
    ],
    image: '/products/hrflare-live-ai.webp',
    category: 'ai',
    customLink: '/software/ai-workflow-automation',
    stack: ['n8n', 'OpenAI', 'Claude', 'MCP', 'Supabase', 'Python']
  },
  {
    id: 'rag-knowledge-assistant',
    title: 'RAG Knowledge Assistants on Your Own Data',
    icon: 'MessagesSquare',
    shortDescription: 'An AI assistant that answers from your own documents and database, with the source attached to every answer.',
    description: 'Most chatbots answer from the internet. Yours should answer from your own documents, policies and data, and show exactly where each answer came from. We build retrieval pipelines over your files, help centre or database, with permission rules so people only see what they are allowed to see.',
    offerings: [
      { name: 'Retrieval Over Your Content', description: 'Documents, PDFs, help centres, Google Drive or database tables.' },
      { name: 'Answers With Sources', description: 'Every answer carries the passage it came from, so your team can check it.' },
      { name: 'Chat Widget or API', description: 'A ready chat interface, or an endpoint your existing app can call.' },
      { name: 'Per-user Access Rules', description: 'Staff and customers only get answers from content they may see.' },
      { name: 'Provider Fallback', description: 'Several model providers wired in, so one outage does not stop the assistant.' }
    ],
    benefits: [
      'Answers your team can verify in one click',
      'Says so when the answer is not in your data',
      'Your content is not used to train models',
      'Works for staff or customer support',
      'The approach behind the HR FLARE assistant'
    ],
    image: '/services/rag-knowledge-assistant.svg',
    category: 'ai',
    stack: ['LangChain', 'pgvector', 'Supabase', 'OpenAI', 'Claude', 'FastAPI']
  },
  {
    id: 'ai-agents-mcp',
    title: 'AI Agents, MCP Servers & Claude Code Setup',
    icon: 'Bot',
    shortDescription: 'AI agents and MCP servers that connect Claude or ChatGPT to your own tools, plus Claude Code set up for your dev team.',
    description: 'Your team already uses AI. We make it useful for your business: MCP servers that let Claude or ChatGPT work inside your own database, ticket system or internal API, agents that take over repeated jobs, and Claude Code configured around your codebase and standards, with permissions so nothing runs that you did not approve.',
    offerings: [
      { name: 'Custom MCP Servers', description: 'Connect Claude or ChatGPT to your database, CRM, ticket system or internal API.' },
      { name: 'AI Agents', description: 'Agents that handle repeated work end to end: triage, reporting, follow-ups.' },
      { name: 'Claude Code Team Setup', description: 'Custom skills, subagents and hooks that follow your own review and release standards.' },
      { name: 'Permissions & Guardrails', description: 'Every action runs as the signed-in user and within the rules you set.' },
      { name: 'Handover Guide & Training', description: 'A written guide and a walkthrough, so your team keeps using it after we leave.' }
    ],
    benefits: [
      'AI that works inside your real tools',
      'Repeated jobs handled automatically',
      'Nothing runs without the permission you set',
      'Works with Claude, ChatGPT and IDE assistants',
      'Documented so your team owns it'
    ],
    image: '/services/ai-agents-mcp.svg',
    category: 'ai',
    stack: ['Claude Code', 'MCP', 'OAuth 2.1', 'Node.js', 'Python', 'GitHub Actions']
  },
  {
    id: 'document-data-extraction',
    title: 'Document & Invoice Data Extraction',
    icon: 'FileText',
    shortDescription: 'OCR and AI that read PDFs, scans and forms and turn them into clean, structured data.',
    description: 'Running OCR on a PDF is the easy part. We build pipelines that handle real documents: many layouts, scans and photos, several documents in one file or one document split across pages. Every record carries a confidence score, so a wrong value never slips through silently.',
    offerings: [
      { name: 'PDF & Scan Extraction', description: 'Fields and line items read from digital PDFs, scans and photos.' },
      { name: 'Splitting & Stitching', description: 'Several documents in one file separated, one document across files joined.' },
      { name: 'Confidence Scores & Review', description: 'Low-confidence records flagged for a person to check.' },
      { name: 'Any Output', description: 'CSV, Excel, JSON, or written straight into your database.' },
      { name: 'Multi-language Documents', description: 'English, German and other languages.' }
    ],
    benefits: [
      'Hours of manual data entry removed',
      'Works across many document layouts',
      'Errors flagged instead of hidden',
      'Results where you need them',
      'Accuracy measured on your own samples first'
    ],
    image: '/services/document-data-extraction.svg',
    category: 'ai',
    stack: ['Python', 'OCR', 'LLM extraction', 'FastAPI', 'PostgreSQL', 'Docker']
  }
];

// 6. Build & Rescue
export const buildRescueServices: Service[] = [
  {
    id: 'property-management-portals',
    title: 'Real Estate & Property Management Portals',
    icon: 'Building2',
    shortDescription: 'Tenant portals, maintenance ticketing, rent collection and e-signed leases in one custom platform.',
    description: 'For landlords, property managers and co-living operators outgrowing spreadsheets. One platform for owners, managers, staff and tenants: dashboards for occupancy and arrears, a tenant portal, maintenance requests routed to contractors, rent collection through a real payment provider, and leases signed digitally.',
    offerings: [
      { name: 'Owner & Manager Dashboard', description: 'Occupancy, arrears, renewals and open maintenance in one view.' },
      { name: 'Tenant Portal', description: 'Lease, balance, documents and requests, without calling the office.' },
      { name: 'Maintenance & Ticketing', description: 'Requests with photos, routed to the right contractor, with a trackable status.' },
      { name: 'Rent Collection & Billing', description: 'Recurring charges, late fees and receipts through a payment provider.' },
      { name: 'Leases & E-signature', description: 'Documents generated from templates, signed and filed against the unit.' },
      { name: 'Applications & Listings', description: 'Public listing pages and online applications with a review pipeline.' }
    ],
    benefits: [
      'One system instead of spreadsheets and group chats',
      'Fewer calls to the office',
      'Every role sees only its own scope',
      'A full audit trail of consequential actions',
      'Built for 1 to 2,000+ units'
    ],
    image: '/services/property-management-portals.svg',
    category: 'software',
    customLink: '/software/property-management-portals',
    stack: ['React', 'TypeScript', 'Supabase', 'PostgreSQL', 'Stripe', 'E-signature']
  },
  {
    id: 'ai-app-rescue',
    title: 'AI-Built App Rescue (Lovable, Bolt, Replit)',
    icon: 'Wrench',
    shortDescription: 'Your app works in the builder and breaks in the real world. We fix what is underneath and deploy it.',
    description: 'AI app builders are good at screens and weak at everything behind them. We fix the part underneath: login that fails for real users, databases without security rules, API keys exposed in the frontend, and apps that will not deploy or show a blank screen in production.',
    offerings: [
      { name: 'Bug Finding & Fixing', description: 'The actual cause found and fixed, with a plain-English note on what was wrong.' },
      { name: 'Row Level Security', description: 'Database rules set up so users can never read each other\'s data.' },
      { name: 'Secrets & API Keys', description: 'Keys moved out of the frontend to somewhere safe.' },
      { name: 'Production Deployment', description: 'Deployed on your own hosting, with the code in your own repository.' },
      { name: 'Keep Building Afterwards', description: 'Changes kept compatible with your builder wherever possible.' }
    ],
    benefits: [
      'Your app working for real users',
      'Your users\' data actually protected',
      'No rebuild from scratch',
      'Code and hosting stay yours',
      'Honest advice if a fix is quick'
    ],
    image: '/services/ai-app-rescue.svg',
    category: 'software',
    stack: ['Lovable', 'Bolt', 'Replit', 'Supabase', 'React', 'Vercel']
  },
  {
    id: 'supabase-backend',
    title: 'Supabase Backend Development',
    icon: 'Database',
    shortDescription: 'Schema, auth, row level security and edge functions, built properly and tested.',
    description: 'Supabase is quick to start and easy to get wrong. We design real schemas, set up auth, and write row level security policies for every role, then test them by trying to read data each role should not see. Migrations live in version control, so your database can always be rebuilt.',
    offerings: [
      { name: 'Database Schema Design', description: 'Proper relationships, indexes and migrations in your repository.' },
      { name: 'Authentication', description: 'Email, Google, magic link, or whatever your app needs.' },
      { name: 'Row Level Security, Tested', description: 'Policies for every role, verified by signing in as each one.' },
      { name: 'Storage & Edge Functions', description: 'File buckets with access rules, and server-side logic where needed.' },
      { name: 'Audit of Existing Projects', description: 'A short written list of what is actually wrong in your current setup.' }
    ],
    benefits: [
      'Data that stays private per user',
      'A database you can rebuild any time',
      'Typed client for your frontend',
      'GDPR-conscious setup',
      'The stack HR FLARE runs on'
    ],
    image: '/services/supabase-backend.svg',
    category: 'software',
    stack: ['Supabase', 'PostgreSQL', 'SQL', 'TypeScript', 'Edge Functions', 'Next.js']
  },
  {
    id: 'saas-mvp',
    title: 'SaaS MVP Development',
    icon: 'Rocket',
    shortDescription: 'A working web app real users can log into: auth, roles, your core feature, admin views, deployed.',
    description: 'You do not need a perfect product, you need a working one. We build full web applications, frontend and backend, with sign-up, roles and permissions, your core feature built properly rather than faked, admin views and email notifications. We built HR FLARE the same way.',
    offerings: [
      { name: 'Full-Stack Web App', description: 'Frontend and backend built together and deployed.' },
      { name: 'Accounts, Roles & Permissions', description: 'Sign-up, login and what each kind of user may do.' },
      { name: 'Core Feature, Built for Real', description: 'Connected to a real database, not dummy data.' },
      { name: 'Admin & Notifications', description: 'Admin views to run the product and automatic emails.' },
      { name: 'Payments & Multi-language', description: 'Subscriptions and a second language when you are ready.' }
    ],
    benefits: [
      'Real users this month, not next year',
      'You own the code and hosting',
      'Built to grow past the MVP',
      'Early reviews before anything is built on top',
      'Proven on our own product, HR FLARE'
    ],
    image: '/products/hrflare-live-dashboard.webp',
    category: 'software',
    stack: ['React', 'Next.js', 'Angular', 'TypeScript', 'Supabase', 'FastAPI']
  }
];
